import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Country, State, City } from "@/types/location";

// Cache configuration
const CACHE_TIME = 1000 * 60 * 30; // 30 minutes
const STALE_TIME = 1000 * 60 * 10; // 10 minutes
const SEARCH_STALE_TIME = 1000 * 30; // 30 seconds for search results
const DEFAULT_CITY_LIMIT = 20;
const ADMIN_CITY_LIMIT = 50;
 
 // Fetch all countries (cached globally)
 export function useCountries() {
   return useQuery({
     queryKey: ["location", "countries"],
     queryFn: async (): Promise<Country[]> => {
       const { data, error } = await supabase
         .from("countries")
         .select("code, name, flag, is_active")
         .eq("is_active", true)
         .order("name");
 
       if (error) throw error;
       return data || [];
     },
     staleTime: STALE_TIME,
     gcTime: CACHE_TIME,
   });
 }
 
 // Fetch states for a country (cached per country)
 export function useStates(countryCode: string | null) {
   return useQuery({
     queryKey: ["location", "states", countryCode],
     queryFn: async (): Promise<State[]> => {
       if (!countryCode) return [];
 
       const { data, error } = await supabase
         .from("states")
         .select("id, code, name, country_code, is_active")
         .eq("country_code", countryCode)
         .eq("is_active", true)
         .order("name");
 
       if (error) throw error;
       return data || [];
     },
     enabled: !!countryCode,
     staleTime: STALE_TIME,
     gcTime: CACHE_TIME,
   });
 }
 
 // Fetch cities with pagination and search (cached per country+state+query)
 export function useCities(
   countryCode: string | null,
   stateId: string | null,
   searchQuery: string = "",
   page: number = 0,
   pageSize: number = 50
 ) {
   return useQuery({
     queryKey: ["location", "cities", countryCode, stateId, searchQuery, page],
     queryFn: async (): Promise<{ cities: City[]; hasMore: boolean }> => {
       if (!countryCode) return { cities: [], hasMore: false };
 
       let query = supabase
         .from("cities")
         .select("id, name, country_code, state_id, latitude, longitude, timezone, is_active")
         .eq("country_code", countryCode)
         .eq("is_active", true);
 
       // Filter by state if provided
       if (stateId) {
         query = query.eq("state_id", stateId);
       }
 
       // Search filter
       if (searchQuery.trim()) {
         query = query.ilike("name", `%${searchQuery.trim()}%`);
       }
 
       // Pagination
       const from = page * pageSize;
       const to = from + pageSize;
 
       const { data, error } = await query
         .order("name")
         .range(from, to);
 
       if (error) throw error;
 
       return {
         cities: (data || []) as City[],
         hasMore: (data?.length || 0) > pageSize,
       };
     },
     enabled: !!countryCode,
     staleTime: STALE_TIME,
     gcTime: CACHE_TIME,
   });
 }
 
// Search cities via Meilisearch edge function
export function useSearchCities(
  countryCode: string | null,
  stateId: string | null,
  searchQuery: string,
  options?: {
    isAdmin?: boolean;
    bypassFilters?: boolean;
  }
) {
  const { isAdmin = false, bypassFilters = false } = options || {};
  
  return useQuery({
    queryKey: ["location", "cities-search", countryCode, stateId, searchQuery, isAdmin, bypassFilters],
    queryFn: async (): Promise<City[]> => {
      // Admin with bypass can search globally without country filter
      const needsCountry = !isAdmin || !bypassFilters;
      if (needsCountry && !countryCode) return [];
      
      // Require at least 2 chars
      if (!searchQuery.trim() || searchQuery.trim().length < 2) return [];

      const limit = isAdmin ? ADMIN_CITY_LIMIT : DEFAULT_CITY_LIMIT;

      // Call edge function
      const { data, error } = await supabase.functions.invoke('place-search', {
        body: {
          q: searchQuery.trim(),
          country: (isAdmin && bypassFilters) ? undefined : countryCode,
          state_id: (isAdmin && bypassFilters) ? undefined : stateId,
          limit,
        },
      });

      if (error) {
        console.error('Place search error:', error);
        // Fallback to direct DB query if edge function fails
        return fallbackCitySearch(countryCode, stateId, searchQuery, limit);
      }

      return (data?.hits || []) as City[];
    },
    enabled: searchQuery.trim().length >= 2 && (isAdmin && bypassFilters ? true : !!countryCode),
    staleTime: SEARCH_STALE_TIME,
    gcTime: CACHE_TIME,
  });
}

// Global city search for admins - uses same edge function without country filter
export function useGlobalCitySearch(
  searchQuery: string,
  isAdmin: boolean
) {
  return useQuery({
    queryKey: ["location", "cities-global-search", searchQuery],
    queryFn: async (): Promise<(City & { country_name?: string; state_name?: string })[]> => {
      if (!searchQuery.trim() || searchQuery.trim().length < 2) return [];

      // Call edge function without country filter for global search
      const { data, error } = await supabase.functions.invoke('place-search', {
        body: {
          q: searchQuery.trim(),
          limit: ADMIN_CITY_LIMIT,
        },
      });

      if (error) {
        console.error('Global place search error:', error);
        return [];
      }

      return (data?.hits || []) as (City & { country_name?: string; state_name?: string })[];
    },
    enabled: isAdmin && searchQuery.trim().length >= 2,
    staleTime: SEARCH_STALE_TIME,
    gcTime: CACHE_TIME,
  });
}

// Fallback to direct Supabase query if Meilisearch is unavailable
async function fallbackCitySearch(
  countryCode: string | null,
  stateId: string | null,
  searchQuery: string,
  limit: number
): Promise<City[]> {
  if (!countryCode) return [];
  
  let query = supabase
    .from("cities")
    .select("id, name, country_code, state_id, latitude, longitude, timezone, is_active")
    .eq("is_active", true)
    .eq("country_code", countryCode);

  if (stateId) {
    query = query.eq("state_id", stateId);
  }

  if (searchQuery.trim()) {
    query = query.ilike("name", `%${searchQuery.trim()}%`);
  }

  const { data, error } = await query.order("name").limit(limit);

  if (error) {
    console.error('Fallback city search error:', error);
    return [];
  }

  return (data || []) as City[];
}

 // Check if a country has states/subdivisions
 export function useHasStates(countryCode: string | null) {
   const { data: states, isLoading } = useStates(countryCode);
   return {
     hasStates: (states?.length || 0) > 0,
     isLoading,
   };
 }